import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Tracks the timestamp (unix seconds) at which the logged-in user last opened
 * their notification center. Anything newer than this is considered "unread"
 * and contributes to the bell's badge count. Stored per-pubkey in localStorage
 * so switching accounts keeps independent read state.
 */
export function useNotificationsRead() {
  const { user } = useCurrentUser();
  const key = user ? `notifications-last-read:${user.pubkey}` : 'notifications-last-read:anon';
  const [lastRead, setLastRead] = useLocalStorage<number>(key, 0);

  // Separately track when the user last dismissed the BADGE for actionable
  // items (crew invites, join requests, pending confirmations). Those items
  // stay visible in their tab until acted upon, but "Mark read" should still
  // clear the red badge — otherwise the badge sticks forever and "Mark read"
  // appears broken. We compare item timestamps against this to decide badging.
  const seenKey = user ? `notifications-seen-at:${user.pubkey}` : 'notifications-seen-at:anon';
  const [seenAt, setSeenAt] = useLocalStorage<number>(seenKey, 0);

  const markAllRead = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastRead(now);
    setSeenAt(now);
  }, [setLastRead, setSeenAt]);

  return { lastRead, seenAt, markAllRead };
}
